import { Injectable } from '@angular/core';
import {
  FormControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import * as CryptoJS from 'crypto-js';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  interval,
} from 'rxjs';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import * as CookieConsent from 'vanilla-cookieconsent';
import { NotificationService } from './notification.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class HelperService {
  private timeSubscription: Subscription = new Subscription();
  private timeListener = new BehaviorSubject<string>('');

  constructor(private notificationService: NotificationService) {}

  copyToClipboard(value: any) {
    navigator.clipboard.writeText(value);

    /* Alert the copied text */
    this.notificationService.info(`Copied`, `text copied to clipboard`);
  }

  formatNgbDate(date: NgbDate | null) {
    return date ? `${date.day}/${date.month}/${date.year}` : '';
  }

  private aesEncrypt(value: string, key: string, iv: string) {
    // This method returns an encrypted string using AES standard or the error encountered
    try {
      return CryptoJS.AES.encrypt(
        CryptoJS.enc.Utf8.parse(value.toString()),
        CryptoJS.enc.Utf8.parse(key),
        {
          keySize: 128 / 8,
          iv: CryptoJS.enc.Utf8.parse(iv),
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        }
      ).toString();
    } catch (e) {
      return e;
    }
  }

  private aesDecrypt(cipherText: any, key: string, iv: string) {
    // This method returns a decrypted string using AES standard or the error encountered
    try {
      return CryptoJS.AES.decrypt(cipherText, CryptoJS.enc.Utf8.parse(key), {
        keySize: 128 / 8,
        iv: CryptoJS.enc.Utf8.parse(iv),
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      }).toString(CryptoJS.enc.Utf8);
    } catch (e) {
      return e || '';
    }
  }

  encryptPassword(password: string) {
    return this.aesEncrypt(
      password,
      environment.auth.password.encKey,
      environment.auth.password.encIv
    );
  }

  decryptPassword(cipherText: any) {
    return this.aesDecrypt(
      cipherText,
      environment.auth.password.encKey,
      environment.auth.password.encIv
    );
  }

  encryptBvn(bvn: string) {
    return this.aesEncrypt(
      bvn,
      environment.auth.bvn.encKey,
      environment.auth.bvn.encIv
    );
  }

  decryptBvn(cipherText: any) {
    return this.aesDecrypt(
      cipherText,
      environment.auth.bvn.encKey,
      environment.auth.bvn.encIv
    );
  }

  validateAllFormFields(formGroup: any) {
    //{1}
    Object.keys(formGroup.controls).forEach((field) => {
      //{2}

      const control = formGroup.get(field); //{3}
      if (
        control instanceof UntypedFormControl ||
        control instanceof FormControl
      ) {
        //{4}
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof UntypedFormGroup) {
        //{5}
        this.validateAllFormFields(control); //{6}
      } else if (control instanceof UntypedFormArray) {
        for (let index = 0; index < control.controls.length; index++) {
          const element = control.controls[index];
          if (element instanceof UntypedFormControl) {
            //{4}
            element.markAsTouched({ onlySelf: true });
          } else if (element instanceof UntypedFormGroup) {
            //{5}
            this.validateAllFormFields(element); //{6}
          }
        }
      }
    });
  }

  // duration is in secs
  startTimer(duration: number): Observable<string> {
    let timer = duration,
      minutes: any,
      seconds: any;
    let val = '';
    this.timeSubscription = interval(1000).subscribe(() => {
      minutes = Math.floor(timer / 60);
      seconds = Math.floor(timer % 60);

      minutes = minutes < 10 ? '0' + minutes : minutes;
      seconds = seconds < 10 ? '0' + seconds : seconds;

      if (parseInt(minutes, 10) <= 0 && parseInt(seconds, 10) <= 0) {
        // notification that there is an error
        timer = 0;
        this.timeSubscription.unsubscribe();
        val = 'EXPIRED';
        this.timeListener.next(val);
      }
      --timer;
      val = `${minutes}:${seconds}`;
      this.timeListener.next(val);
    });
    return this.timeListener.asObservable();
  }

  formatToNgbDate(value: any) {
    const date = new Date(value);
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
  }

  cookieConsentInit() {
    CookieConsent.run({
      // root: 'body',
      // autoShow: true,
      // disablePageInteraction: true,
      // hideFromBots: true,
      // mode: 'opt-in',
      // revision: 0,

      cookie: {
        name: 'breeze_cookie',
        domain: 'https://nrc.tps.ng',
        path: '/',
        sameSite: 'Lax',
        expiresAfterDays: 182,
      },

      // guiOptions: {
      //   consentModal: {
      //     layout: 'cloud inline',
      //     position: 'bottom center',
      //     equalWeightButtons: true,
      //     flipButtons: false,
      //   },
      //   preferencesModal: {
      //     layout: 'box',
      //     equalWeightButtons: true,
      //     flipButtons: false,
      //   },
      // },
      guiOptions: {
        consentModal: {
          layout: 'cloud', // box/cloud/bar
          position: 'bottom center', // bottom/middle/top + left/right/center
          flipButtons: false, // enable to invert buttons
        },
        preferencesModal: {
          layout: 'box', // box/bar
        },
      },

      onFirstConsent: ({ cookie }) => {},

      onConsent: ({ cookie }) => {
        const changedCategories = cookie.categories;
        if (changedCategories.includes('analytics')) {
          (window as any).clarity('consent');
        } else if (!changedCategories.includes('analytics')) {
          (window as any).clarity('consent', false);
        }
      },

      onChange: ({ changedCategories, changedServices }) => {
        if (changedCategories.includes('analytics')) {
          (window as any).clarity('consent');
        } else if (!changedCategories.includes('analytics')) {
          (window as any).clarity('consent', false);
        }
      },

      categories: {
        necessary: {
          enabled: true, // this category is enabled by default
          readOnly: true, // this category cannot be disabled
        },
        analytics: {
          autoClear: {
            cookies: [
              {
                name: 'CLID', // regex: match all cookies starting with '_ga'
              },
              {
                name: 'MUID', // string: exact cookie name
              },
            ],
          },
          // https://cookieconsent.orestbida.com/reference/configuration-reference.html#category-services
          services: {
            // ga: {
            //   label: 'Microsoft Clarity',
            //   onAccept: () => {},
            //   onReject: () => {},
            // },
          },
        },
        // ads: {},
      },

      language: {
        default: 'en',
        translations: {
          en: {
            consentModal: {
              title: 'Cookies and Privacy',
              description: `The Breeze website uses cookies to provide necessary site functionality and improve your online experience. By using this website, you agree to the use of cookies as outlined in our online <a class="cc-link" target="_blank" href="https://nrc.tps.ng/#/privacypolicy">privacy policy</a>. You can update your preferences <a type="button" data-cc="show-preferencesModal" class="cc-link">manage preferences.</a>`,
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject all',
              showPreferencesBtn: 'Manage preferences',
              // closeIconLabel: 'Reject all and close modal',
              // footer: `
              //         <a class="cc-link" target="_blank" href="https://nrc.tps.ng/#/privacypolicy">privacy policy</a>
              //     `,
            },
            preferencesModal: {
              title: 'cookie preferences',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject all',
              savePreferencesBtn: 'Save settings',
              closeIconLabel: 'Close modal',
              serviceCounterLabel: 'Service|Services',
              sections: [
                {
                  title: 'Cookie usage 📢',
                  description: `Cookies are small files placed on your device by websites to record your browsing history. They usually do not contain personal identification but can be linked to your stored personal data. Additionally, browsers collect unique information like browser type and hardware used, enabling tracking of your interactions with websites and apps.`,
                },
                {
                  title: 'Strictly Necessary',
                  description:
                    'These cookies are essential for the proper functioning of my website. Without these cookies, the website would not work properly',

                  linkedCategory: 'necessary',
                },
                {
                  title: 'Performance and Analytics',
                  description:
                    'These cookies collect information about how you use our website. All of the data is anonymized and cannot be used to identify you, it also allows the website to remember the choices you have made in the past.',
                  linkedCategory: 'analytics',
                  cookieTable: {
                    // caption: 'Cookie table',
                    headers: {
                      name: 'Cookie',
                      domain: 'Domain',
                      exp: 'Expiration',
                      desc: 'Description',
                    },
                    body: [
                      {
                        name: 'CLID',
                        domain: 'www.clarity.ms',
                        exp: '1 year',
                        desc: 'Description 1',
                      },
                      {
                        name: 'MUID',
                        domain: 'www.clarity.ms',
                        exp: '1 year',
                        desc: 'Description 2',
                      },
                      {
                        name: '_clsk',
                        domain: 'www.clarity.ms',
                        exp: '1 year',
                        desc: 'Description 2',
                      },
                    ],
                  },
                },
                // {
                //   title: 'Targeting and Advertising',
                //   description:
                //     'These cookies are used to make advertising messages more relevant to you and your interests. The intention is to display ads that are relevant and engaging for the individual user and thereby more valuable for publishers and third party advertisers.',
                //   linkedCategory: 'ads',
                // },
                {
                  title: 'More information',
                  description:
                    'For any queries in relation to my policy on cookies and your choices, please contact us at <a href="mailto:Customercare@tps.ng">Customercare@tps.ng</a> or <a href="tel:+2342018887741">+234(0)2018887741</a>',
                },
              ],
            },
          },
        },
      },
    });
  }
}
